import {
    ReconciliationSummaryResponse,
    TransactionsPairForReconciliation,
} from "@common/contracts/reconciliation.contract";
import { toUTCDateOnly } from "@common/helpers/date";
import { getStatmentTransactionId } from "@common/helpers/reconciliation";
import {
    canCashItemClearOnDate,
    getCashItemClearedBeforeDateMessage,
} from "@common/helpers/reconciliationCashItems";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { BankCashItemType } from "@common/types/bankCashItem";
import { isLockedByDateOnly } from "@helpers/lock";
import {
    CheckMergeSelection,
    TransactionToMatch,
} from "./Reconciliation.context";

interface IsReconciliationCashItemParams {
    transaction: Pick<TransactionListItem, "id" | "cashItem">;
    summary: Pick<
        ReconciliationSummaryResponse,
        "outstandingCashItems" | "transactionsPairs"
    >;
}

export interface FindTransactionCandidateParams {
    transaction: Pick<
        TransactionListItem,
        | "id"
        | "amount"
        | "originalAmount"
        | "date"
        | "dateOnly"
        | "financialAccountId"
        | "reconciledInId"
        | "cashItem"
    >;
    transactionToMatch: TransactionToMatch | undefined;
    financialAccountId: number;
    lockDate: Date | null;
    summary: Pick<
        ReconciliationSummaryResponse,
        "outstandingCashItems" | "transactionsPairs"
    >;
}

export function getReconciliationCashItemType({
    transaction,
    summary,
}: IsReconciliationCashItemParams): BankCashItemType | undefined {
    return (
        transaction.cashItem?.type ??
        summary.outstandingCashItems.find(
            ({ financialTransactionId }) =>
                financialTransactionId === transaction.id,
        )?.type
    );
}

export function isReconciliationCashItem(
    params: IsReconciliationCashItemParams,
): boolean {
    return (
        getReconciliationCashItemType(params) !== undefined ||
        params.summary.transactionsPairs.some(
            ({ fromAccount }) =>
                fromAccount?.financialTransactionId === params.transaction.id &&
                Boolean(fromAccount.isCashItem),
        )
    );
}

export function getFindTransactionCandidateTooltip({
    transaction,
    transactionToMatch,
    financialAccountId,
    lockDate,
    summary,
}: FindTransactionCandidateParams): string | undefined {
    if (
        transaction.originalAmount !==
        transactionToMatch?.statementTransactionToMatch.amount
    ) {
        return "Transaction amount is different";
    }
    const dateOnly = transaction.dateOnly ?? toUTCDateOnly(transaction.date);
    if (
        isReconciliationCashItem({ transaction, summary }) &&
        !canCashItemClearOnDate({
            cashItemDateOnly: dateOnly,
            clearedDateOnly:
                transactionToMatch?.statementTransactionToMatch.date,
        })
    ) {
        return getCashItemClearedBeforeDateMessage({
            type: getReconciliationCashItemType({ transaction, summary }),
            cashItemDateOnly: dateOnly,
        });
    }
    if (isLockedByDateOnly(lockDate, dateOnly)) {
        return "Transaction is locked";
    }
    if (transaction.financialAccountId !== financialAccountId) {
        return "Transaction belongs to a different account than the reconciled one";
    }
    if (transaction.reconciledInId) {
        return "Transaction is already reconciled against another statement";
    }
    return undefined;
}

export function isFindTransactionCandidateDisabled(
    params: FindTransactionCandidateParams,
): boolean {
    const { transaction, transactionToMatch } = params;
    return (
        transaction.id ===
            transactionToMatch?.currentlyMatchedFinancialTransactionId ||
        transaction.amount !==
            transactionToMatch?.statementTransactionToMatch.amount ||
        getFindTransactionCandidateTooltip(params) !== undefined
    );
}

interface MatchToStatementTransactionParams {
    pairs: TransactionsPairForReconciliation[];
    statementTransactionId: string;
    financialTransactionId: number;
}

interface DropStaleCheckMergesParams {
    pairs: TransactionsPairForReconciliation[];
    checkMerges: CheckMergeSelection[];
    statementTransactionsToAddIdsSet: Set<string>;
    financialTransactionsToRemoveIdsSet: Set<number>;
}

export function matchToStatementTransaction({
    pairs,
    statementTransactionId,
    financialTransactionId,
}: MatchToStatementTransactionParams):
    | TransactionsPairForReconciliation[]
    | undefined {
    const statementPairIndex = pairs.findIndex(
        ({ fromStatement }) =>
            fromStatement &&
            getStatmentTransactionId(fromStatement) === statementTransactionId,
    );
    const accountTransaction = pairs.find(
        ({ fromAccount }) =>
            fromAccount?.financialTransactionId === financialTransactionId,
    )?.fromAccount;
    const statementTransaction = pairs[statementPairIndex]?.fromStatement;
    if (
        statementPairIndex === -1 ||
        !statementTransaction ||
        !accountTransaction ||
        pairs[statementPairIndex].fromAccount?.financialTransactionId ===
            financialTransactionId ||
        (accountTransaction.isCashItem &&
            !canCashItemClearOnDate({
                cashItemDateOnly: accountTransaction.date,
                clearedDateOnly: statementTransaction.date,
            }))
    ) {
        return undefined;
    }

    return pairs.flatMap((pair, index) => {
        if (index === statementPairIndex) {
            const matchedPair = { ...pair, fromAccount: accountTransaction };
            return pair.fromAccount
                ? [{ fromAccount: pair.fromAccount }, matchedPair]
                : [matchedPair];
        }
        if (
            pair.fromAccount?.financialTransactionId !== financialTransactionId
        ) {
            return [pair];
        }
        return pair.fromStatement
            ? [{ fromStatement: pair.fromStatement }]
            : [];
    });
}

export function dropStaleCheckMerges({
    pairs,
    checkMerges,
    statementTransactionsToAddIdsSet,
    financialTransactionsToRemoveIdsSet,
}: DropStaleCheckMergesParams): CheckMergeSelection[] {
    const replacedTransactionIds = new Set(
        pairs.flatMap(({ fromStatement, fromAccount }) =>
            fromStatement &&
            fromAccount &&
            statementTransactionsToAddIdsSet.has(
                getStatmentTransactionId(fromStatement),
            )
                ? [fromAccount.financialTransactionId]
                : [],
        ),
    );

    return checkMerges.filter((merge) =>
        "statementTransactionId" in merge
            ? statementTransactionsToAddIdsSet.has(merge.statementTransactionId)
            : !financialTransactionsToRemoveIdsSet.has(
                  merge.bankTransactionId,
              ) && !replacedTransactionIds.has(merge.bankTransactionId),
    );
}
